import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Check, ChevronDown, Pencil } from 'lucide-react'
import type { BidDriverSelection } from '@numeo/types'
import { BodyEmailTemplate, CanonicalLoad } from '@numeo/types-core'
import { BoxTruckPricingRequest } from '@numeo/types'
import { useAuth } from 'hooks/auth'
import { log } from 'utils/logger'
import { Button, Tabs } from '@/components/ds2'
import { OfferBookEmailCompose } from 'features/load-hub/components/expanded-row/OfferBookEmailCompose'
import { resolveBodyTemplates, resolveSavedTemplate, resolveSenderMailbox, templateKeyOf } from 'features/load-hub/components/expanded-row/savedTemplate'
import { BOX_TRUCK_DEFAULT_TEMPLATES } from '../constants/emailTemplates'
import { BoxTruckEmailThread, recordBoxTruckPricingDecision } from '../services/boxTruckLoadsApi'
import { PrivateLoadEmailChat } from 'features/load-hub/components/expanded-row/PrivateLoadEmailChat'
import { PrivateLoadEmailSelector } from 'features/load-hub/components/PrivateLoadEmailSelector'
import { EmailConfig, getCurrentEmailConfig, setCurrentEmailConfig } from 'features/load-hub/hooks/usePrivateLoadEmail'
import { fetchEmailThreadIds } from 'features/load-hub/api/loads.api'
import { resolveSenderCompanyData } from 'features/load-hub/utils/resolveSenderCompanyData'
import { extractSignatureLogoSrc } from '@/components/tiptap/utils'
import { PlaceBidCalculator } from './PlaceBidCalculator'
import { TemplateEditModal } from './TemplateEditModal'
import { READ_ONLY_TEMPLATE_BODY } from './ReadOnlyEmailBody'
import { brokerEmail, sourceGroupKey } from '../utils/loadFields'
import { BoxTruckEmailType, trackBoxTruckEmailSent, trackBoxTruckUiInteraction } from '../utils/analytics'
import { buildTemplatePreviewValues } from '../utils/templatePreview'

/** Cap for the Info tab's label when it carries a picked template's name. */
const INFO_LABEL_MAX = 18

/** One row of the Info tab's template menu — name (+ subject sublabel) with a check on the active pick. */
const TemplateMenuItem = ({ label, sublabel, active, onPick }: { label: string; sublabel?: string; active: boolean; onPick: () => void }) => (
    <button type="button" onClick={onPick} className="flex w-full items-center gap-2 px-3 py-1.5 text-left transition-colors hover:bg-default">
        <span className="min-w-0 flex-1">
            <span className="block truncate text-sm text-foreground">{label}</span>
            {sublabel && <span className="block truncate text-[11px] text-foreground-muted">{sublabel}</span>}
        </span>
        {active && <Check size={14} className="shrink-0 text-accent" />}
    </button>
)
/** The thread view's tab key — joins the compose tabs once a bid email exists, so sending (say) Info
 *  doesn't take Offer/Book away. */
const CHAT_KEY = 'chat'
type EmailTabKey = BoxTruckEmailType | typeof CHAT_KEY
/** Send-button label per type — "Place Bid" keeps the offer flow's bidding language. */
const SEND_LABELS: Record<BoxTruckEmailType, string> = { info: 'Send Info', offer: 'Place Bid', book: 'Send Booking' }

interface DraftEmailTabProps {
    load: CanonicalLoad
    /** Close the parent load workspace as soon as a valid email request starts. */
    onActionComplete: () => void
    /** The selected driver's id — part of the compose key so picking/clearing a driver re-seeds the body. */
    selectedDriverKey?: string
    /** The PICKED driver, recorded with the bid. Null while nothing is picked — the nearest-driver
     *  fallback feeds `driverVariables` for display and must not be reported as the quoted unit. */
    bidDriver?: BidDriverSelection | null
    /** The checked driver's own bid on this load — seeds the calculator's Price for Driver (see PlaceBidCalculator). */
    driverBidPrice?: number | null
    /** Driver template variables interpolated into the compose on top of the load/company variables.
     *  Unit details retain the picked-or-nearest fallback. miles_out needs a PICKED driver, but not a
     *  successful route: it falls back to that driver's direct estimate while the route is pending,
     *  failed or superseded — unmarked, since this text reaches the broker. */
    driverVariables?: Record<string, string | null | undefined>
    /** This load's thread as the BOARD already resolved it — the contact column's overlay reads the very
     *  same endpoint. Present → open on it instead of asking again for one load. */
    seedThread?: BoxTruckEmailThread | null
    /** The request behind the suggestion the panel had on screen, or null when it showed none. Posted
     *  with the rate that actually went out — the only moment the two exist together — and re-priced
     *  server-side so no figure the browser displayed is taken on trust. */
    pricingRequest?: BoxTruckPricingRequest | null
    /** Whether a suggestion was on screen, for the send event. */
    pricingShown?: boolean
}

/**
 * Draft Email tab — the bid workspace. With no thread yet it opens straight into the Place Bid
 * calculator + compose (no intermediate button); once a bid email exists it becomes the Load-Hub
 * thread (PrivateLoadEmailChat) so replies continue there. Thread id is resolved via
 * fetchEmailThreadIds([externalId]) on open and after a send (compose doesn't return it).
 */
export const DraftEmailTab = ({
    load,
    onActionComplete,
    selectedDriverKey,
    bidDriver,
    driverBidPrice,
    driverVariables,
    seedThread,
    pricingRequest,
    pricingShown,
}: DraftEmailTabProps) => {
    const [threadId, setThreadId] = useState<string | null>(null)
    const [mongoId, setMongoId] = useState<string | undefined>(undefined)
    const [checking, setChecking] = useState(true)
    const [threadError, setThreadError] = useState(false)
    const [bidPrice, setBidPrice] = useState<number | null>(() => (load.rateAmountUsd != null ? Number(load.rateAmountUsd) : null))
    // Which broker-email flavour is composed; switching tabs re-seeds the compose (subject + body). Defaults
    // to Info — the first-touch availability inquiry — so the operator opens on the inquiry, not the bid.
    // The compose still needs a type in the default state (it drives the price pass and the send path).
    // 'offer' is that type: the operator arrives having priced the load, and offer is the one type that
    // carries the number — Info is a rate-less availability inquiry by design (see rateOverride below).
    const [emailType, setEmailType] = useState<BoxTruckEmailType>('offer')
    // The selected top tab. null = automatic: the thread (once one exists) else the Info compose. Explicit
    // picks stick, so an operator can move Chat → Offer → Book freely after the first send.
    const [activeTab, setActiveTab] = useState<EmailTabKey | null>(null)
    const [editOpen, setEditOpen] = useState(false)
    // Bumped after a template save so the compose remounts and re-seeds from the edited template.
    const [templateVersion, setTemplateVersion] = useState(0)
    // The Info tab's picked template (users create MULTIPLE named templates in Settings — the Info tab is
    // a dropdown over ALL of them). null = the default resolution (the INFO-type template, else bundled).
    const [templateId, setTemplateId] = useState<string | null>(null)
    const [templateMenuOpen, setTemplateMenuOpen] = useState(false)
    const templateMenuRef = useRef<HTMLDivElement>(null)
    const { user, application, organizationData } = useAuth()
    // The selected "Send as" sender email (from the reused load-hub selector). Feeding it into the
    // compose key remounts it so the subject/body/signature re-seed from that sender's company data
    // and templates — the selector already wrote the choice to the shared email-config singleton.
    const [senderEmail, setSenderEmail] = useState('')
    // Stable so the selector's init/select effect doesn't churn on every render (load-hub passes a
    // memoized handler too). Writes the choice to the shared singleton the compose reads on remount.
    const handleEmailConfigChange = useCallback((config: EmailConfig) => {
        setCurrentEmailConfig(config)
        setSenderEmail(config.selectedEmail)
    }, [])
    // Host for the compose's Cancel/Send buttons in the side-by-side layout — they portal under the
    // calculator (left) so they stay fixed and visible without scrolling the compose.
    const [actionsEl, setActionsEl] = useState<HTMLDivElement | null>(null)
    // Once a valid send starts, the drawer closes without waiting for the email-service response. Suppress
    // the compose's later onClose callback while React processes the parent close state.
    const sendStartedRef = useRef(false)

    const email = brokerEmail(load)
    const externalId = load.externalId
    const sourceType = load.sourceType

    // The sender's saved templates — resolveBodyTemplates is the SAME resolution the compose runs, so
    // what this dropdown lists is always resolvable there. Usable = seedable (subject + body); legacy
    // rows predate `_id`, so identity is templateKeyOf — and duplicate keys (same name+subject, no _id)
    // are dropped after the first, matching which one the compose's `find` would pick anyway.
    const templates = useMemo<BodyEmailTemplate[]>(() => {
        const nylas = resolveSenderMailbox(user?.nylasEmails, senderEmail)
        const seen = new Set<string>()
        return resolveBodyTemplates(nylas, user).filter((t) => {
            if (!t.body || !t.subject) return false
            const key = templateKeyOf(t)
            if (seen.has(key)) return false
            seen.add(key)
            return true
        })
    }, [user, senderEmail])
    const pickedTemplate = templateId ? (templates.find((t) => templateKeyOf(t) === templateId) ?? null) : null
    // Does this sender have an Offer email of their own?
    //
    // Asked of the SAME resolver the compose resolves with, not a second reading of it: this decides
    // whether to hand the compose `forceBundledTemplate`, so two implementations would mean the drawer
    // showing one email while the send carries another — the bug this flag exists to fix.
    //
    // Not read off `templates` either, which keys on `senderEmail` and is empty until the sender selector
    // has reported in; that gap reads as "no template" on first paint, seeds the bundled card and re-seeds
    // a moment later, which the operator sees as a flash of the wrong email.
    const hasSavedOfferTemplate = useMemo(() => {
        const config = getCurrentEmailConfig()
        return Boolean(
            resolveSavedTemplate(user, { type: 'offer', selectedEmail: senderEmail || config?.selectedEmail, selectedTemplateId: config?.selectedTemplateId })
        )
    }, [user, senderEmail])
    const selectedNylasEmail = user?.nylasEmails?.find((entry) => entry.email === senderEmail)
    const companySignature =
        templates.find((template) => template.signature && extractSignatureLogoSrc(template.signature))?.signature ||
        templates.find((template) => template.signature)?.signature ||
        BOX_TRUCK_DEFAULT_TEMPLATES[emailType].signature
    const previewCompanyData = useMemo(
        () =>
            resolveSenderCompanyData({
                selectedCompanyDetail: selectedNylasEmail?.companyDetail,
                nylasEmails: user?.nylasEmails,
                application,
                companyFromParentEnabled: organizationData?.companyFromParentEnabled,
                userName: user?.name || '',
                userEmail: senderEmail,
                companyLogoUrl: extractSignatureLogoSrc(companySignature) || '',
            }),
        [selectedNylasEmail, user?.nylasEmails, user?.name, application, organizationData?.companyFromParentEnabled, senderEmail, companySignature]
    )
    const templatePreviewValues = useMemo(
        () => buildTemplatePreviewValues(bidPrice != null ? { ...load, rateAmountUsd: bidPrice } : load, previewCompanyData, driverVariables),
        [bidPrice, load, previewCompanyData, driverVariables]
    )

    // Close the template menu on outside click (it renders in-flow under the tabs, not portaled).
    useEffect(() => {
        if (!templateMenuOpen) return
        const onDown = (e: MouseEvent) => {
            if (!templateMenuRef.current?.contains(e.target as Node)) setTemplateMenuOpen(false)
        }
        document.addEventListener('mousedown', onDown)
        return () => document.removeEventListener('mousedown', onDown)
    }, [templateMenuOpen])

    /**
     * Which thread lookup is allowed to write state.
     *
     * Bumped by every lookup AND by a seed arriving, so a resolution that is no longer the latest answer is
     * dropped instead of landing on top of a newer one. Without it the drawer can lose a thread it is already
     * showing: a lookup started before the board's overlay landed resolves afterwards, and because
     * `fetchEmailThreadIds` turns a failed request into `{}`, "no answer" is written as "no thread" — a fresh
     * compose in front of a live broker conversation, which is how the same broker gets contacted twice.
     */
    const threadLookupRef = useRef(0)

    const refreshThread = useCallback(async () => {
        if (!externalId) {
            setChecking(false)
            return
        }
        const lookup = ++threadLookupRef.current
        const isCurrent = () => threadLookupRef.current === lookup
        setChecking(true)
        setThreadError(false)
        try {
            // Box Truck bids send with force_new_thread, so the thread lives in PG interactions under the
            // composite id — pass sourceType so the server resolves it (bare ids would default to DAT).
            const map = await fetchEmailThreadIds([{ externalId, sourceType }])
            if (!isCurrent()) return
            const entry = map[externalId]
            // No client-side fallback needed: the send endpoint awaits the interaction write,
            // so the thread is already readable by the time the send responds
            // (dat-ext.controller.ts → dat-ext.service.ts, writeThreadIdAfterSend). Caching the id here as
            // well would be a second mechanism for a guarantee the server already makes.
            setThreadId(entry?.emailThreadId ?? null)
            setMongoId(entry?.loadId)
        } catch {
            // Never leave the spinner stuck: show an error and let the user retry.
            if (isCurrent()) setThreadError(true)
        } finally {
            // Whoever is current owns the spinner — a superseded lookup must not clear it out from under them.
            if (isCurrent()) setChecking(false)
        }
    }, [externalId, sourceType])

    const seededThreadId = seedThread?.emailThreadId ?? null
    const seededLoadId = seedThread?.loadId

    useEffect(() => {
        // A thread the board already resolved is the same answer this endpoint would give — the contact
        // column's overlay reads it from the same lookup — so take it and skip the round trip.
        //
        // A MISS is deliberately NOT treated as an answer. The overlay has no entry both for a load nobody
        // has emailed and for a load whose overlay slice failed or was truncated by the server's id cap, and
        // showing the second as "no thread yet" would hide a live broker conversation behind a fresh compose.
        // So only a positive hit short-circuits; anything else still asks.
        if (seededThreadId) {
            // Supersede any lookup already in flight. The seed can arrive AFTER this component asked for
            // itself — the overlay lands a moment later, or refreshes while the drawer is open — and that
            // older answer must not overwrite the thread being shown now.
            threadLookupRef.current++
            setThreadId(seededThreadId)
            setMongoId(seededLoadId)
            setThreadError(false)
            setChecking(false)
            return
        }
        void refreshThread()
    }, [refreshThread, seededThreadId, seededLoadId])

    const handleSendStarted = useCallback(() => {
        sendStartedRef.current = true
        onActionComplete()
    }, [onActionComplete])

    // Success analytics still wait for the server response even though the drawer is already closed.
    const handleSent = useCallback(
        (info?: { alreadySent?: boolean; sentRate?: number | null; bodyEdited?: boolean }) => {
            trackBoxTruckEmailSent({ email_type: emailType, source: sourceGroupKey(load), pricing_shown: pricingShown === true })
            // The pricing pilot's feedback loop: what we suggested, beside what the dispatcher actually sent.
            //
            // Two sends are NOT recorded at all. Info, because it is a rate-less availability inquiry and
            // would read as a dispatcher quoting $0 against the suggestion. And a DEDUPLICATED send,
            // because nothing went out, and counting it twice would inflate that dispatcher's row on a
            // page read as a verdict on them. A hand-edited body IS recorded, flagged, and left out of the
            // scoring server-side — see `bodyEdited`.
            const bidRate = info?.sentRate ?? bidPrice
            if (!pricingRequest || emailType === 'info' || info?.alreadySent || !bidRate || bidRate <= 0) return
            void recordBoxTruckPricingDecision({
                externalId: load.externalId ?? String(load.id ?? ''),
                sourceType: load.sourceType ?? '',
                // The request that produced what was on screen. The server re-prices it and records its
                // own answer; no figure the panel displayed is taken from the browser.
                quote: pricingRequest,
                originCity: load.pickupCity ?? undefined,
                destinationCity: load.deliveryCity ?? undefined,
                bidRate,
                bodyEdited: info?.bodyEdited === true,
            }).catch((error: unknown) => log.w('[box-truck-pricing] decision not recorded', { error: error instanceof Error ? error.message : String(error) }))
        },
        [emailType, load, pricingRequest, pricingShown, bidPrice]
    )

    const handleResent = useCallback(
        (outcome: 'sent' | 'failed') => trackBoxTruckUiInteraction(outcome === 'sent' ? 'email_resent' : 'email_resend_failed', sourceGroupKey(load)),
        [load]
    )

    // Compose's onClose fires right after onSent; skip the re-query while the drawer is closing. A genuine
    // Cancel (no send) re-evaluates the thread state as before.
    const handleClose = useCallback(() => {
        if (sendStartedRef.current) return
        void refreshThread()
    }, [refreshThread])

    if (!email) {
        return <p className="py-8 text-center text-sm text-foreground-muted">No broker email on this load — can't draft an email.</p>
    }

    if (checking) {
        return (
            <div className="flex items-center justify-center py-8">
                <span className="h-5 w-5 animate-spin rounded-full border-2 border-accent border-t-transparent" />
                <span className="ml-2 text-sm text-foreground-muted">Loading thread…</span>
            </div>
        )
    }

    if (threadError) {
        return (
            <div className="rounded-lg border border-dashed border-border px-4 py-8 text-center">
                <p className="text-sm font-medium text-danger">Couldn’t load the email thread</p>
                <Button type="button" variant="ghost" size="sm" onClick={() => void refreshThread()} className="mt-2">
                    Retry
                </Button>
            </div>
        )
    }

    // The selected top tab, or NULL for the default state: no tab picked, the board's own bid template in
    // the compose. Nothing was selectable-but-unselected before — the derivation forced 'info', so opening
    // the drawer always landed on an availability inquiry even though this board exists to send a price.
    // Once a thread exists it joins the tab row as "Email Chat" and takes the default instead; sending the
    // first email must NOT take the other compose tabs away (the operator still needs Offer / Book after an
    // Info inquiry). Without a thread a stale 'chat' pick (thread resolution failed after a cancel) falls
    // back to the default.
    const selectedTab: EmailTabKey | null = threadId ? (activeTab ?? CHAT_KEY) : activeTab === CHAT_KEY ? null : activeTab
    // The Info tab doubles as the template DROPDOWN: its label is the picked template's name (or "Info"),
    // and clicking it while already active opens the menu with every saved template.
    const infoLabelRaw = pickedTemplate ? pickedTemplate.name || pickedTemplate.subject || 'Template' : 'Info'
    const infoLabel = infoLabelRaw.length > INFO_LABEL_MAX ? `${infoLabelRaw.slice(0, INFO_LABEL_MAX - 1)}…` : infoLabelRaw
    const emailTabs = [
        { key: 'info', label: infoLabel, suffix: templates.length > 0 ? <ChevronDown size={13} /> : undefined },
        { key: 'offer', label: 'Offer' },
        { key: 'book', label: 'Book' },
    ]
    const tabItems = threadId ? [{ key: CHAT_KEY, label: 'Email Chat' }, ...emailTabs] : emailTabs

    // Layout: calculator column (tabs + calculator + Send) beside the compose, which owns its own scroll
    // (`h-full overflow-y-auto`) — the row must NOT wrap it in a second overflow.
    // Testing hook: with a `testEmail` in localStorage, route the bid to yourself instead of the broker.
    // Gated to non-production so a stale key can never silently redirect a real broker bid in prod.
    const testEmail = process.env.NODE_ENV !== 'production' ? localStorage.getItem('testEmail')?.trim() || undefined : undefined
    // The pencil edits the TYPE template (Info/Offer/Book) — hidden while a dropdown template is composing on
    // Info, where editing the invisible INFO-type template would appear to do nothing (the override keeps
    // winning resolution). Manage picked templates in Settings. It lives in the compose's To/From row, not the
    // tabs row: beside 4 tabs (with a thread) the 320px calculator column has no room for it, so the row
    // wrapped and left the pencil stranded on a line of its own.
    const editTemplateButton =
        selectedTab === 'info' && pickedTemplate ? null : (
            <button
                type="button"
                onClick={() => setEditOpen(true)}
                aria-label="Edit template"
                title="Edit template"
                className="flex size-8 shrink-0 items-center justify-center rounded-lg border border-border text-foreground-muted transition-colors hover:bg-default hover:text-foreground"
            >
                <Pencil size={15} />
            </button>
        )
    const renderCompose = (actionsContainer?: HTMLElement | null) => (
        <OfferBookEmailCompose
            // Remount on tab switch or template save (both fully change the template), and on driver select/
            // clear so the unit block seeds in/out. Sender change is NOT keyed — it re-seeds in place via
            // senderKey below so an in-progress draft isn't discarded.
            // The forced-default flag is part of the key: going from the default state to the Offer TAB keeps
            // `emailType` at 'offer', so without it neither the remount nor the re-seed would fire and the
            // operator's own saved template would never appear.
            key={`${emailType}-${selectedTab === null ? 'default' : 'picked'}-${hasSavedOfferTemplate ? 'own' : 'bundled'}-${templateVersion}-${pickedTemplate ? templateKeyOf(pickedTemplate) : ''}-${selectedDriverKey ?? ''}`}
            load={load}
            type={emailType}
            // The Info tab's dropdown pick — an explicit template that wins the compose's resolution.
            // Derived from the RESOLVED pick, not the raw id: a sender switch can invalidate the stored id
            // (the new sender's list lacks it), and passing it anyway would leave the compose half-overridden
            // (persist skipped, pencil hidden) while actually seeding the default.
            templateOverrideId={emailType === 'info' && pickedTemplate ? templateKeyOf(pickedTemplate) : undefined}
            // Info is a rate-less availability inquiry — no bid price. The selected driver's unit block is added
            // on EVERY type (Info/Offer/Book), so picking a driver always reflects in the body the operator sees.
            rateOverride={emailType === 'info' ? undefined : bidPrice}
            bidDriver={bidDriver}
            // No bodyPrefix: the driver's unit info reaches the body only via the {{driver_*}} template
            // variables now — the old auto-prepended "Miles out / Dims / Payload / Type" block is retired.
            extraVariables={driverVariables}
            // This board's own bundled templates — the bid card. Load Hub keeps the shared defaults.
            defaultTemplates={BOX_TRUCK_DEFAULT_TEMPLATES}
            // Default state (no tab picked): fall back to the board's bundled template only when the sender
            // has no Offer template of their own. The bundled card used to win here unconditionally, which
            // meant a tenant that had deliberately authored its own bid email still sent the stock card to
            // every broker unless the operator happened to click the Offer tab first — the two paths showed
            // different mail from the same drawer, with nothing on screen to say which one was active.
            forceBundledTemplate={selectedTab === null && !hasSavedOfferTemplate}
            // The board's template is designed HTML — nested styled panels, not paragraphs. Without this the
            // editor parses it into its own schema, drops every div and style, and emits that back as the
            // body it sends, so the card is lost on screen AND in the broker's inbox.
            preserveHtml
            // HTML the editor would reshape (column definitions) is shown read-only and sent as authored. A table
            // layout is edited in place like the bid card — shrunk to fit, since it is wider than this pane.
            readOnlyBody={READ_ONLY_TEMPLATE_BODY}
            fitBodyToWidth
            analyticsSurface="box_truck"
            // The card ends in its own company footer, so the signature block would print the company a
            // second time under it. Hidden and left off the sent mail for the DEFAULT state only — picking
            // Info / Offer / Book restores it exactly as before.
            hideSignature={selectedTab === null}
            sendLabel={SEND_LABELS[emailType]}
            sendPrimary
            toOverride={testEmail}
            onClose={handleClose}
            onSendStarted={handleSendStarted}
            onSent={handleSent}
            actionsContainer={actionsContainer}
            // "Send as" picker sits under the From line; the drawer closes via its own chrome so no Cancel.
            senderSelector={<PrivateLoadEmailSelector showTemplateSelector={false} compactEmailPicker onConfigChange={handleEmailConfigChange} />}
            headerAction={editTemplateButton}
            hideCancel
            // The bottom-pane compose is chrome-less: the tabs already name the email type, the drawer owns
            // close, CC is never used for bids, and the send CTA fills the calculator column.
            hideHeader
            hideCc
            fullWidthSend
            // Drives the in-place re-seed on sender change (no remount), preserving the draft.
            senderKey={senderEmail}
        />
    )
    // (Email Chat /) Info / Offer / Book selector. The Edit-template pencil and the "Send as" sender picker live
    // inside the compose (its To/From row). The modal portals itself, so rendering it once here covers both
    // layouts.
    const tabsBar = (
        // flex-wrap: with a thread the row gains an "Email Chat" tab and can exceed the 320px calculator
        // column — wrapping beats clipping. relative: hosts the Info tab's template menu.
        <div ref={templateMenuRef} className="relative flex shrink-0 flex-wrap items-center gap-2">
            {/* Compact hugging tabs — the wide bottom pane doesn't need a full-width tab bar. */}
            <Tabs
                variant="primary"
                items={tabItems}
                selectedKey={selectedTab ?? 'offer'}
                onSelectionChange={(k) => {
                    // Info is also the template dropdown: a click while it's ALREADY the active compose
                    // toggles the menu instead of re-selecting.
                    if (k === 'info' && selectedTab === 'info' && templates.length > 0) {
                        setTemplateMenuOpen((o) => !o)
                        return
                    }
                    setTemplateMenuOpen(false)
                    setActiveTab(k as EmailTabKey)
                    if (k !== CHAT_KEY) setEmailType(k as BoxTruckEmailType)
                }}
            />
            {templateMenuOpen && (
                <div className="absolute left-0 top-full z-20 mt-1.5 max-h-72 w-72 overflow-y-auto rounded-xl border border-border bg-surface py-1 shadow-overlay max-[899px]:right-0 max-[899px]:w-auto max-[899px]:max-w-[calc(100vw-24px)]">
                    <div className="px-3 pb-1 pt-1.5 text-[10px] font-semibold uppercase tracking-wider text-foreground-muted">Email template</div>
                    <TemplateMenuItem
                        label="Info (default)"
                        active={templateId === null}
                        onPick={() => {
                            setTemplateId(null)
                            setTemplateMenuOpen(false)
                        }}
                    />
                    {templates.map((t) => (
                        <TemplateMenuItem
                            key={templateKeyOf(t)}
                            label={t.name || t.subject || 'Untitled'}
                            sublabel={t.subject && t.name !== t.subject ? t.subject : undefined}
                            active={templateId === templateKeyOf(t)}
                            onPick={() => {
                                setTemplateId(templateKeyOf(t))
                                setTemplateMenuOpen(false)
                            }}
                        />
                    ))}
                </div>
            )}
        </div>
    )
    const editModal = editOpen ? (
        <TemplateEditModal
            emailType={emailType}
            previewValues={templatePreviewValues}
            onClose={() => setEditOpen(false)}
            onSaved={() => {
                setActiveTab(emailType)
                setTemplateVersion((version) => version + 1)
            }}
        />
    ) : null

    // Thread tab → the bid conversation continues here (reply + respond), just like Load Hub, with the
    // compose tabs staying available above it. Fill the height so the chat's compose input pins to the
    // bottom (message list scrolls internally).
    if (threadId && selectedTab === CHAT_KEY) {
        return (
            <div className="flex min-h-0 flex-1 flex-col gap-3">
                {tabsBar}
                <div className="flex min-h-0 flex-1 flex-col">
                    <PrivateLoadEmailChat load={load} emailThreadId={threadId} loadMongoId={mongoId} isExpanded allowResend onResent={handleResent} />
                </div>
            </div>
        )
    }
    return (
        // Desktop stays side-by-side: the tabs live INSIDE the left calculator column and the compose starts
        // at the pane's top. On mobile the same two regions stack so neither keeps a desktop-only fixed width.
        <div className="flex min-h-0 flex-1 flex-col gap-3 min-[900px]:flex-row">
            <div className="flex w-full shrink-0 flex-col gap-2 min-[900px]:w-[320px]">
                {tabsBar}
                <div className="min-h-0 overflow-y-auto pr-0.5">
                    <PlaceBidCalculator load={load} onBidChange={setBidPrice} driverBidPrice={driverBidPrice} driverBidKey={selectedDriverKey} />
                </div>
                {/* Full-width Send, directly UNDER the calculator (portaled in by the compose) — not pinned
                    to the pane bottom, so it stays in reach however tall the pane is dragged. */}
                <div ref={setActionsEl} />
            </div>
            <div className="flex min-h-[420px] min-w-0 flex-1 min-[900px]:min-h-0">{renderCompose(actionsEl)}</div>
            {editModal}
        </div>
    )
}
